/*
 * Product rail kinds (sections/tcs-product-rail.liquid): tabs that swap the shelf between the section's own
 * products and each kind's collection, and point the head's link at what's showing.
 *
 * The cards for each kind wait in a <template data-kind-panel>; the first tab's cards are the ones the page
 * arrived with. Arrow keys, Home and End move between tabs (one tab stop for the row).
 *
 * Motion, on the shared speeds: one thumb glides and resizes from the old tab to the new ("space"); the
 * old cards leave at the exit speed, then the new ones rise in one after another, so the row reads as
 * replaced rather than blinked. On a phone the row keeps the chosen tab in the middle and fades at an edge
 * that has more tabs past it. With reduced motion the thumb and cards simply change.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SPACE = 'cubic-bezier(0.32, 0.72, 0, 1)';

  function init(root) {
    const kinds = root.querySelector('[data-kinds]');
    const tabs = kinds ? [...kinds.querySelectorAll('[role="tab"]')] : [];
    const track = root.querySelector('[data-shelf-track]');
    if (!tabs.length || !track || root.dataset.kindsReady) return;
    root.dataset.kindsReady = '1';
    const panels = { popular: track.innerHTML };
    root.querySelectorAll('template[data-kind-panel]').forEach((t) => { panels[t.dataset.kindPanel] = t.innerHTML; });
    const link = root.querySelector('.tcs-section-head .tcs-link');

    // The thumb: one surface behind the tabs that moves to the chosen one.
    const thumb = document.createElement('span');
    thumb.className = 'tcs-kinds__thumb';
    thumb.setAttribute('aria-hidden', 'true');
    kinds.prepend(thumb);
    const place = (tab) => {
      kinds.style.setProperty('--thumb-x', `${tab.offsetLeft}px`);
      kinds.style.setProperty('--thumb-w', `${tab.offsetWidth}px`);
    };
    const current = () => tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
    place(current());
    // Only once it sits under the first tab does it take over from the tab's own background, and only then
    // does it animate, so it never slides in from nowhere on load.
    requestAnimationFrame(() => kinds.setAttribute('data-glide', ''));
    new ResizeObserver(() => place(current())).observe(kinds);
    document.fonts?.ready.then(() => place(current()));

    // Edges: fade the side that has more tabs past it.
    const edges = () => {
      const max = kinds.scrollWidth - kinds.clientWidth;
      kinds.toggleAttribute('data-more-start', kinds.scrollLeft > 2);
      kinds.toggleAttribute('data-more-end', kinds.scrollLeft < max - 2);
    };
    kinds.addEventListener('scroll', edges, { passive: true });
    window.addEventListener('resize', edges, { passive: true });
    edges();

    let swap = 0;
    const show = (html) => {
      track.innerHTML = html;
      track.scrollLeft = 0;
      track.dispatchEvent(new Event('scroll'));
      if (reduce.matches) return;
      // The cards in view rise in a quick sequence; the rest are off to the right and simply arrive.
      const right = track.getBoundingClientRect().right;
      [...track.children].forEach((item, i) => {
        if (item.getBoundingClientRect().left > right) return;
        item.animate(
          [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }],
          { duration: 320, delay: i * 45, easing: SPACE, fill: 'backwards' },
        );
      });
    };

    const choose = (tab, focus) => {
      if (tab.getAttribute('aria-selected') === 'true') return;
      for (const t of tabs) {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      }
      if (focus) tab.focus({ preventScroll: true });
      place(tab);
      // Keep the chosen tab in the middle of the row, without moving the page.
      const left = tab.offsetLeft - (kinds.clientWidth - tab.offsetWidth) / 2;
      kinds.scrollTo({ left: Math.max(0, left), behavior: reduce.matches ? 'auto' : 'smooth' });

      const html = panels[tab.dataset.kind] || '';
      const mine = ++swap;
      if (reduce.matches) show(html);
      else {
        track.getAnimations({ subtree: true }).forEach((a) => a.cancel());
        track.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' })
          .finished.then(() => {
            if (mine !== swap) return;
            track.getAnimations().forEach((a) => a.cancel());
            show(html);
          }, () => {});
      }
      if (link && tab.dataset.linkUrl) {
        link.href = tab.dataset.linkUrl;
        const text = [...link.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.nodeValue.trim());
        if (text) text.nodeValue = tab.dataset.linkLabel;
      }
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => choose(tab, false));
      tab.addEventListener('keydown', (e) => {
        const to = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
        if (to === undefined) return;
        e.preventDefault();
        choose(tabs[(to + tabs.length) % tabs.length], true);
      });
    });
  }

  const start = () => document.querySelectorAll('[data-shelf]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-shelf]').forEach(init));
})();
